import React, { useState, useEffect, useRef } from 'react';
import type { Wine } from '../types';
import { getWineCharacteristicsV2, extractWineDataFromImage } from '../services/geminiService';
import { optimizeImage, getBase64Size } from '../utils/imageOptimizer';

interface AddWineFormProps {
    onAddWine: (wine: Omit<Wine, 'id'>) => void;
}

const initialWineState: Omit<Wine, 'id'> = {
    name: '',
    type: '赤',
    origin: '',
    variety: '',
    price: 0,
    taste: 'ミディアムボディ',
    characteristics: [],
    image: '',
    visible: true,
    isGlassWine: false,
    glassPrice: null,
};

const AddWineForm: React.FC<AddWineFormProps> = ({ onAddWine }) => {
    const [wine, setWine] = useState(initialWineState);
    const [charInput, setCharInput] = useState('');
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isFetchingChars, setIsFetchingChars] = useState(false);
    const [isCharManuallyEdited, setIsCharManuallyEdited] = useState(false);
    const [isExtractingFromPhoto, setIsExtractingFromPhoto] = useState(false);

    const debounceTimeout = useRef<number | null>(null);

    // AIによる特徴の自動取得
    useEffect(() => {
        if (wine.name && wine.origin && wine.variety && !isCharManuallyEdited) {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            setIsFetchingChars(true);
            debounceTimeout.current = window.setTimeout(async () => {
                try {
                    const characteristicsV2 = await getWineCharacteristicsV2({
                        id: `temp-${Date.now()}`,
                        name: wine.name,
                        origin: wine.origin,
                        variety: wine.variety,
                        type: wine.type,
                        taste: wine.taste
                    });
                    if (characteristicsV2.length > 0 && !isCharManuallyEdited) {
                        // V2からlabelのみ抽出して旧形式に変換
                        const labels = characteristicsV2.map(c => c.label);
                        setCharInput(labels.join(', '));
                    }
                } catch (e) {
                    console.error("Failed to fetch wine characteristics:", e);
                } finally {
                    setIsFetchingChars(false);
                }
            }, 1000);
        } else {
             if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            setIsFetchingChars(false);
        }

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [wine.name, wine.origin, wine.variety, isCharManuallyEdited]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        
        if (['name', 'origin', 'variety'].includes(name)) {
            setIsCharManuallyEdited(false);
            setCharInput('');
        }

        setWine(prev => {
            let newValue: any = value;
            
            if (type === 'number') {
                const numValue = parseFloat(value);
                newValue = isNaN(numValue) ? null : numValue;
            } else if (type === 'checkbox') {
                const checked = (e.target as HTMLInputElement).checked;
                newValue = checked;
                // グラスワインが無効になったらグラス価格をnullに
                if (name === 'isGlassWine' && !checked) {
                    return { 
                        ...prev, 
                        [name]: newValue,
                        glassPrice: null
                    };
                }
            }
            
            return { 
                ...prev, 
                [name]: newValue 
            };
        });
    };
    
    const handleCharInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCharInput(e.target.value);
        if (!isCharManuallyEdited) {
            setIsCharManuallyEdited(true);
        }
    };

    const handlePhotoExtract = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // ファイルサイズチェック（5MB制限）
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setError(`画像ファイルが大きすぎます（最大5MB）。現在のサイズ: ${Math.round(file.size / 1024 / 1024 * 10) / 10}MB`);
            e.target.value = '';
            return;
        }

        // ファイル形式のチェック
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('サポートされていないファイル形式です。JPEG、PNG、GIF、WebP形式の画像を選択してください。');
            e.target.value = '';
            return;
        }

        setError('');
        setSuccess('');
        setIsExtractingFromPhoto(true);

        try {
            // 画像をBase64に変換
            const imageBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const result = e.target?.result as string;
                    if (result) {
                        resolve(result);
                    } else {
                        reject(new Error('ファイルの読み込みに失敗しました'));
                    }
                };
                reader.onerror = () => {
                    reject(new Error('ファイルの読み込みに失敗しました'));
                };
                reader.readAsDataURL(file);
            });

            // プレビューを設定
            setImagePreview(imageBase64);

            // Gemini APIで画像解析
            console.log('📸 画像からワインデータ抽出開始...');
            const extractedData = await extractWineDataFromImage(imageBase64);
            console.log('📸 画像からワインデータ抽出完了:', extractedData);

            // 抽出されたデータをフォームに自動入力
            setWine(prev => ({
                ...prev,
                name: extractedData.name || prev.name,
                type: extractedData.type || prev.type,
                origin: extractedData.origin || prev.origin,
                variety: extractedData.variety || prev.variety,
                price: extractedData.price && extractedData.price > 0 ? extractedData.price : prev.price,
                taste: extractedData.taste || prev.taste,
                image: extractedData.image || prev.image,
                isGlassWine: extractedData.isGlassWine !== undefined ? extractedData.isGlassWine : prev.isGlassWine,
                glassPrice: extractedData.glassPrice && extractedData.glassPrice > 0 ? extractedData.glassPrice : prev.glassPrice,
            }));

            setSuccess('画像からワイン情報を抽出しました。内容を確認して必要に応じて修正してください。');
            setTimeout(() => setSuccess(''), 5000);
        } catch (error: any) {
            console.error('📸 画像解析エラー:', error);
            setError(error.message || '画像の解析に失敗しました。もう一度お試しください。');
            setImagePreview(null);
        } finally {
            setIsExtractingFromPhoto(false);
            e.target.value = ''; // 同じファイルを再度選択できるようにリセット
        }
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const file = e.target.files?.[0];
            if (file) {
            console.log('=== 画像アップロード開始 ===');
            console.log('ファイル名:', file.name);
            console.log('ファイルサイズ:', Math.round(file.size / 1024), 'KB');
            console.log('ファイルタイプ:', file.type);
            
            // 即座にファイルサイズチェック（800KB）
            const maxSize = 800 * 1024; // 800KB
            if (file.size > maxSize) {
                const fileSizeKB = Math.round(file.size / 1024);
                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
                
                // エラーメッセージを即座に表示
                setError(`❌ 画像ファイルが大きすぎます！\n\n📊 ファイル情報:\n• 現在のサイズ: ${fileSizeKB}KB (${fileSizeMB}MB)\n• 制限サイズ: 800KB以下\n\n📱 iPhone写真の縮小方法:\n1. 写真アプリで画像を開く\n2. 「編集」→「サイズ調整」\n3. サイズを「小」または「中」に設定\n4. 「完了」をタップ\n\n💡 その他の方法:\n• 画像圧縮アプリを使用\n• スクリーンショットで撮り直し\n• 別の小さな画像を選択\n\n⚠️ この画像は処理できません。800KB以下の画像を選択してください。`);
                
                // ファイル入力をクリア
                e.target.value = '';
                return;
            }
            
            // ファイル形式のチェック
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                setError('サポートされていないファイル形式です。JPEG、PNG、GIF、WebP形式の画像を選択してください。');
                return;
            }
            
            try {
                // 800KB以下のファイルは最適化をスキップして直接処理
                console.log('ファイルサイズが800KB以下のため、最適化をスキップします');
                const optimizedImage = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const result = e.target?.result as string;
                        if (result) {
                            resolve(result);
                        } else {
                            reject(new Error('ファイルの読み込みに失敗しました'));
                        }
                    };
                    reader.onerror = () => {
                        reject(new Error('ファイルの読み込みに失敗しました'));
                    };
                    reader.readAsDataURL(file);
                });
                
                // サイズをログ出力
                const originalSize = Math.round(file.size / 1024);
                const base64Size = getBase64Size(optimizedImage);
                console.log(`画像処理: ${String(originalSize)}KB → Base64: ${String(base64Size)}KB`);
                console.log('Base64長:', optimizedImage.length);
                console.log('Base64プレフィックス:', optimizedImage.substring(0, 50) + '...');
                
                setWine(prev => ({ ...prev, image: optimizedImage }));
                setImagePreview(optimizedImage);
                setError(''); // エラーをクリア
                console.log('=== 画像アップロード完了（最適化スキップ） ===');
                console.log('画像がローカル状態に保存されました');
            } catch (error) {
                console.error('画像の読み込みに失敗しました:', error);
                setError('画像の読み込みに失敗しました。別の画像を試してください。');
            }
            }
        } catch (outerError) {
            console.error('画像処理で予期しないエラーが発生しました:', outerError);
            setError('画像の処理中にエラーが発生しました。ページを再読み込みしてから再度お試しください。');
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!wine.name || !wine.origin || !wine.variety) {
            setError('ワイン名、産地、品種は必須です。');
            return;
        }
        
        if (wine.price <= 0) {
            setError('価格は0より大きい値を入力してください。');
            return;
        }

        const characteristics = charInput.split(',').map(s => s.trim()).filter(Boolean);
        if (characteristics.length === 0) {
            setError('特徴を少なくとも1つ入力してください（カンマ区切り）。');
            return;
        }

        console.log('=== ワイン追加開始 ===');
        console.log('ワイン名:', wine.name);
        console.log('画像データの有無:', wine.image ? 'あり' : 'なし');
        if (wine.image) {
            console.log('画像データの長さ:', wine.image.length);
            console.log('画像データのプレフィックス:', wine.image.substring(0, 50) + '...');
        }
        
        // データ正規化：undefinedをnullに変換、空文字列をnullに変換
        const normalizedWine = {
            ...wine,
            characteristics,
            // 文字列フィールドの空文字列をnullに変換
            name: wine.name.trim() || null,
            origin: wine.origin.trim() || null,
            variety: wine.variety.trim() || null,
            image: wine.image.trim() || null,
            // 数値フィールドの検証と正規化
            price: wine.price > 0 ? wine.price : null,
            glassPrice: wine.isGlassWine && wine.glassPrice && wine.glassPrice > 0 ? wine.glassPrice : null,
        };
        
        onAddWine(normalizedWine);
        
        console.log('=== ワイン追加完了 ===');
        console.log('ワインがローカル状態に追加されました');
        console.log('画像データも含めてFirestoreに同期される予定です');
        
        setSuccess(`「${String(wine.name || '')}」がリストに追加されました。`);
        setWine(initialWineState);
        setCharInput('');
        setImagePreview(null);
        setIsCharManuallyEdited(false);
        const imageInput = document.getElementById('image-upload') as HTMLInputElement;
        if(imageInput) imageInput.value = '';

        setTimeout(() => setSuccess(''), 3000);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {error && <p className="text-red-600 font-medium text-sm sm:text-base whitespace-pre-line">! {error}</p>}
            {success && <p className="text-green-700 font-medium text-sm sm:text-base">✓ {success}</p>}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">ワイン名</label>
                    <input type="text" name="name" id="name" value={wine.name} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
                 <div>
                    <label htmlFor="origin" className="block text-sm font-medium text-gray-700">産地</label>
                    <input type="text" name="origin" id="origin" value={wine.origin} onChange={handleChange} required placeholder="例: フランス" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
                <div>
                    <label htmlFor="variety" className="block text-sm font-medium text-gray-700">品種</label>
                    <input type="text" name="variety" id="variety" value={wine.variety} onChange={handleChange} required placeholder="例: シャルドネ" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
                 <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">タイプ</label>
                    <select name="type" id="type" value={wine.type} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500">
                        <option>赤</option>
                        <option>白</option>
                        <option>ロゼ</option>
                        <option>スパークリング</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700">ボトル価格 (円)</label>
                    <input type="number" name="price" id="price" value={wine.price} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500" />
                </div>
                
                {/* グラスワイン機能 */}
                <div className="space-y-3">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isGlassWine"
                            name="isGlassWine"
                            checked={wine.isGlassWine || false}
                            onChange={handleChange}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isGlassWine" className="ml-2 block text-sm font-medium text-gray-700">
                            グラスワインとして提供
                        </label>
                    </div>
                    
                    {wine.isGlassWine && (
                        <div>
                            <label htmlFor="glassPrice" className="block text-sm font-medium text-gray-700">グラス価格 (円)</label>
                            <input
                                type="number"
                                name="glassPrice"
                                id="glassPrice"
                                value={wine.glassPrice || ''}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                                placeholder="グラス価格を入力"
                            />
                        </div>
                    )}
                </div>
                <div>
                    <label htmlFor="taste" className="block text-sm font-medium text-gray-700">味</label>
                    <select name="taste" id="taste" value={wine.taste} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500">
                        <option>フルボディ</option>
                        <option>ミディアムボディ</option>
                        <option>ライトボディ</option>
                        <option>辛口</option>
                        <option>甘口</option>
                        <option>やや甘口</option>
                    </select>
                </div>
                 <div className="sm:col-span-2">
                    <label htmlFor="characteristics" className="block text-sm font-medium text-gray-700">特徴 (カンマ区切り)</label>
                    <div className="relative">
                        <input 
                          type="text" 
                          name="characteristics" 
                          id="characteristics" 
                          value={charInput} 
                          onChange={handleCharInputChange}
                          placeholder={isFetchingChars ? "特徴を生成中..." : "例: カシス, スミレ, エレガント"} 
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
                          disabled={isFetchingChars}
                        />
                        {isFetchingChars && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-red-800 rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>
                </div>
                 <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">ワインの画像</label>
                    <div className="mt-1 flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        {imagePreview ? (
                            <img src={imagePreview} alt="Preview" className="h-16 w-16 sm:h-20 sm:w-20 object-contain rounded-md bg-gray-100" />
                        ) : (
                            <div className="h-16 w-16 sm:h-20 sm:w-20 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-xs text-center p-1">
                               画像なし
                            </div>
                        )}
                        <div className="flex flex-col sm:flex-row gap-2">
                            <label htmlFor="image-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                <span>ファイルを選択</span>
                                <input 
                                    id="image-upload" 
                                    name="image" 
                                    type="file" 
                                    className="sr-only" 
                                    accept="image/*" 
                                    onChange={handleImageChange}
                                    onInput={(e) => {
                                        // ファイル選択時の即座チェック
                                        const file = (e.target as HTMLInputElement).files?.[0];
                                        if (file && file.size > 800 * 1024) {
                                            const fileSizeKB = Math.round(file.size / 1024);
                                            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
                                            alert(`⚠️ ファイルサイズが大きすぎます！\n\n現在のサイズ: ${fileSizeKB}KB (${fileSizeMB}MB)\n制限サイズ: 800KB以下\n\n800KB以下の画像を選択してください。`);
                                            (e.target as HTMLInputElement).value = '';
                                        }
                                    }}
                                />
                            </label>
                            <label 
                                htmlFor="photo-extract" 
                                className={`cursor-pointer bg-red-800 text-white py-2 px-3 border border-red-800 rounded-md shadow-sm text-sm leading-4 font-medium hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-700 flex items-center gap-2 ${isExtractingFromPhoto ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {isExtractingFromPhoto ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>解析中...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 001.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span>📷 写真から追加</span>
                                    </>
                                )}
                                <input 
                                    id="photo-extract" 
                                    type="file" 
                                    className="sr-only" 
                                    accept="image/*" 
                                    onChange={handlePhotoExtract}
                                    disabled={isExtractingFromPhoto}
                                />
                            </label>
                        </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                        「写真から追加」を選択すると、AIが画像からワイン情報を自動抽出してフォームに入力します
                    </p>
                </div>
            </div>
            
            <div className="flex justify-center sm:justify-end">
                <button type="submit" className="w-full sm:w-auto bg-red-800 text-white font-bold py-2 px-6 rounded-lg shadow-md hover:bg-red-900 transition-colors duration-300 text-sm sm:text-base">
                    ワインを追加
                </button>
            </div>
        </form>
    );
};

export default AddWineForm;
